import Vue from 'vue';
import ICog from 'src/assets/cog.svg';
import { Prop, Component, Watch, Inject } from 'vue-property-decorator';
import type CascadeFilterManager from 'src/framework/model/extensions/lookup-field/cascade-filter-manager';
import type { FormsModel } from 'src/framework/forms-model';
import { EntryStatus } from './EntryStatus';

@Component({
	name: 'CCascadeFilter',
	components: { ICog },
	model: {
		prop: 'value',
		event: 'change'
	}
})
export default class CascadeFilter extends Vue {
	@Prop()
	value: any | any[];

	@Prop()
	manager: CascadeFilterManager;

	@Prop({ default: false })
	readonly: boolean;

	@Inject()
	formsModel: FormsModel;

	@Prop()
	entryStatus: EntryStatus;

	loading = false;
	slot = { value: '', values: [] };

	async created() {
		this.loading = true;
		await this.manager.ready;
		this.loading = false;
	}

	get isList() {
		return Array.isArray(this.value);
	}

	get choiceOptions() {
		const locale = this.formsModel.model.$locale || 'en';
		const valueSet = new Set(
			this.manager.filteredIndexes
				.flatMap(idx => this.manager.extract(idx))
				.sort(function (a, b) {
					if (typeof a === 'string')
						return a.localeCompare(b, locale);
					else {
						if (a < b) return -1;
						if (a > b) return 1;
						return 0;
					}
				})
				.map(val => this.manager.stringify(val))
		);
		return Array.from(valueSet.values())
			.map(value => ({ displayValue: value, value }));
	}

	@Watch('value', { immediate: true })
	valueUpdated() {
		if (this.isList)
			this.slot.values = (this.value as any[]).map(val => this.manager.stringify(val));
		else
			this.slot.value = this.manager.stringify(this.value);
	}

	@Watch('slot.value')
	async valueUpdatedByUser(value) {
		if (this.loading)
			return;

		// Exit if the form is currently rendering (due to entry changing on the entries page)
		if (this.entryStatus === EntryStatus.Pending)
			return;

		if (!value)
			this.$emit('change', null);
		else if (value !== this.manager.stringify(this.value))
			this.$emit('change', this.manager.parse(value));
	}

	@Watch('slot.values')
	async listValueUpdatedByUser(values: string[]) {
		if (this.loading)
			return;

		// Exit if the form is currently rendering (due to entry changing on the entries page)
		if (this.entryStatus === EntryStatus.Pending)
			return;

		const currentVals = (this.value as any[]).map(val => this.manager.stringify(val));
		if (this.isList && (values.length !== currentVals.length || !values.every(v => currentVals.includes(v))))
			this.$emit('change', values.map(this.manager.parse));
	}
};